import BottomSheet, { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { Portal } from "@gorhom/portal";
import { Button, ListItem, Text, withTheme } from "@rneui/themed";
import React, { useContext, useRef } from "react";
import { Platform, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FileType, useUserUUID } from "@annunciate/core";
import { useIsEntitledQuery } from "@annunciate/query";
import { DownloadButton } from "@annunciate/ui/dist/components/content/DownloadButton";
import { FavoriteButton } from "@annunciate/ui/dist/components/content/FavoriteButton";
import Config from '@annunciate/core/dist/utils/config';
import { BottomSheetBackdrop, FAIcon, NavigationContext, ProgressButton, } from "../../..";
import { useTheme } from "../../../hooks";
import { normalizeText } from "../../../utils/scaling-utils";
import { BookmarkListButton } from "../../bookmark";
import { ListItemAvatar } from "../../list/ListItemAvatar";
import { ShareItemButton } from "../../shared";
import { TagButton } from "../../tag";
const UnthemedEllipsisButton = ({ size = normalizeText(25), buttonStyle, iconStyle, listIconSize = normalizeText(25), showDownload: defaultShowDownload = true, showMarkAsPlayed: defaultShowMarkAsPlayed = true, item, children = null, }) => {
    const { top, bottom } = useSafeAreaInsets();
    const isEntitled = useIsEntitledQuery(item);
    const showAuthenticated = useUserUUID();
    const progressRef = useRef(undefined);
    const favoriteRef = useRef(undefined);
    const tagsRef = useRef(undefined);
    const downloadRef = useRef(undefined);
    const bookmarkRef = useRef(undefined);
    const shareRef = useRef(undefined);
    const { colors } = useTheme();
    const bottomSheetRef = useRef(null);
    const showAccessSpecificControls = item && isEntitled === true;
    const supportsDownloads = item.asset?.type !== FileType.VIDEO;
    const showDownload = Platform.OS !== "web" && showAccessSpecificControls && supportsDownloads;
    const supportsBookmarks = item.asset?.type &&
        [FileType.AUDIO, FileType.EBOOK].includes(item.asset?.type);
    const showShare = Platform.OS === 'web' || Config.APP_WEB_FQDN;
    const { onNavigateToItem } = useContext(NavigationContext);
    const uri = item?.cover?.src;
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { type: "clear", onPress: () => bottomSheetRef.current?.expand(), style: buttonStyle },
            React.createElement(FAIcon, { name: "ellipsis-v", size: size, style: [
                    iconStyle,
                    {
                        width: size,
                        textAlign: "center",
                    },
                ] })),
        React.createElement(Portal, { hostName: "modal" },
            React.createElement(BottomSheet, { ref: bottomSheetRef, backdropComponent: BottomSheetBackdrop, index: -1, topInset: top, activeOffsetY: Platform.OS === "android" ? [-5, 5] : undefined, failOffsetX: Platform.OS === "android" ? [-5, 5] : undefined, enablePanDownToClose: true, backgroundStyle: { backgroundColor: colors.backgroundPrimary }, handleIndicatorStyle: { backgroundColor: colors.textPrimary }, containerStyle: { zIndex: 3000 } },
                React.createElement(BottomSheetScrollView, { style: {
                        backgroundColor: colors.backgroundPrimary,
                    }, contentContainerStyle: { paddingBottom: bottom } },
                    React.createElement(ListItem, null,
                        React.createElement(ListItemAvatar, { source: uri ? { uri } : undefined, isLocked: false }),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null, item.name))),
                    showAuthenticated && (React.createElement(ListItem, { onPress: () => favoriteRef.current?.toggle() },
                        React.createElement(IconWrapper, { iconSize: listIconSize },
                            React.createElement(FavoriteButton, { size: listIconSize, item: item, ref: favoriteRef })),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null,
                                React.createElement(Text, { bold: false }, "Add to Favorites"))))),
                    showAuthenticated && (React.createElement(ListItem, { onPress: () => tagsRef.current?.toggle() },
                        React.createElement(IconWrapper, { iconSize: listIconSize },
                            React.createElement(TagButton, { size: listIconSize, item: item, ref: tagsRef })),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null,
                                React.createElement(Text, { bold: false }, "Tag"))))),
                    showAuthenticated && supportsBookmarks && (React.createElement(ListItem, { onPress: () => bookmarkRef.current?.toggle() },
                        React.createElement(IconWrapper, { iconSize: listIconSize },
                            React.createElement(BookmarkListButton, { size: listIconSize, item: item, ref: bookmarkRef, closeParent: () => {
                                    bottomSheetRef.current?.forceClose({ duration: 0 });
                                } })),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null,
                                React.createElement(Text, { bold: false }, "Bookmarks"))))),
                    showAccessSpecificControls && (React.createElement(React.Fragment, null,
                        defaultShowMarkAsPlayed && showAuthenticated && (React.createElement(ListItem, { onPress: () => progressRef.current?.toggle() },
                            React.createElement(IconWrapper, { iconSize: listIconSize },
                                React.createElement(ProgressButton, { size: listIconSize, ref: progressRef, item: item })),
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null,
                                    React.createElement(Text, { bold: false }, "Mark Played/Unplayed"))))),
                        showDownload && defaultShowDownload && (React.createElement(ListItem, { onPress: () => downloadRef.current?.toggle() },
                            React.createElement(IconWrapper, { iconSize: listIconSize },
                                React.createElement(DownloadButton, { size: listIconSize, ref: downloadRef, item: item })),
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null,
                                    React.createElement(Text, { bold: false }, "Download/Delete"))))))),
                    showShare && (React.createElement(ListItem, { onPress: () => shareRef.current?.toggle() },
                        React.createElement(IconWrapper, { iconSize: listIconSize },
                            React.createElement(ShareItemButton, { size: listIconSize, item: item, ref: shareRef })),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null,
                                React.createElement(Text, { bold: false }, "Share"))))),
                    React.createElement(ListItem, { onPress: () => {
                            onNavigateToItem(item);
                            bottomSheetRef.current?.close();
                        } },
                        React.createElement(IconWrapper, { iconSize: listIconSize },
                            React.createElement(FAIcon, { size: listIconSize, style: { paddingVertical: 10 }, name: "arrow-right" })),
                        React.createElement(ListItem.Content, null,
                            React.createElement(ListItem.Title, null,
                                React.createElement(Text, { bold: false }, "Go to item")))),
                    children)))));
};
const IconWrapper = ({ iconSize, children, }) => {
    const { buttonPaddingHorizontal } = useTheme();
    return (React.createElement(View, { style: {
            width: iconSize + buttonPaddingHorizontal * 2,
            alignItems: "center",
        } }, children));
};
export const EllipsisButton = withTheme(UnthemedEllipsisButton, "EllipsisButton");
//# sourceMappingURL=EllipsisButton.js.map